import type { FilterModel } from 'ag-grid-community';
import { convertFilterModelToBackend } from '@/queries/ssrm/convertFilterModelToBackend';
import BaseService from '../BaseService';

export interface SsrmMetricsResponse<TRow> {
  rowData: TRow[];
  rowCount?: number;
}

export interface CncSummaryMetricsRow {
  cncSummaryId?: number;
  requestId?: number;
  category?: string;
  submissionDate?: string;
  requestedAmount?: number;
  mainCurrency?: string;
  clientName?: string;
  childCount?: number;
}

export type SsrmColumnVO = {
  id: string;
  displayName: string;
  field: string;
  aggFunc?: string;
  dateTrunc?: 'DAY' | 'MONTH' | 'QUARTER' | 'YEAR';
};

export type SsrmFilterModel = Record<string, unknown>;

export interface SsrmRequestBody {
  startRow: number;
  endRow: number;
  rowGroupCols: unknown[];
  groupKeys: unknown[];
  valueCols: unknown[];
  pivotCols: unknown[];
  pivotMode: boolean;
  filterModel?: Record<string, unknown>;
  sortModel: Array<{ colId: string; sort: 'asc' | 'desc' }>;
  rowIdExtractor?: string;
  convertedFilterModel?: Array<Record<string, unknown>> | null;
  groupingMode?: 'FLAT' | 'HIERARCHICAL';
  havingModel?: Record<string, unknown>;
}

const SSRM_PAGE_SIZE = 500;
const FETCH_CONCURRENCY = 5;

async function fetchAllSsrmRows<TRow>(
  fetchPage: (startRow: number, endRow: number) => Promise<SsrmMetricsResponse<TRow>>,
): Promise<TRow[]> {
  const first = await fetchPage(0, SSRM_PAGE_SIZE);
  const firstRows = first.rowData ?? [];

  if (first.rowCount == null) {
    if (firstRows.length < SSRM_PAGE_SIZE) {
      return firstRows;
    }
    throw new Error(
      `fetchAllSsrmRows: SSRM response missing rowCount on a full page (${firstRows.length} rows); cannot determine total.`,
    );
  }

  const total = first.rowCount;

  if (total <= SSRM_PAGE_SIZE) {
    return firstRows;
  }

  const ranges: Array<[number, number]> = [];
  for (let start = SSRM_PAGE_SIZE; start < total; start += SSRM_PAGE_SIZE) {
    ranges.push([start, Math.min(start + SSRM_PAGE_SIZE, total)]);
  }

  const results: TRow[][] = [firstRows];
  for (let i = 0; i < ranges.length; i += FETCH_CONCURRENCY) {
    const chunk = ranges.slice(i, i + FETCH_CONCURRENCY);
    const pages = await Promise.all(chunk.map(([start, end]) => fetchPage(start, end)));
    results.push(...pages.map(p => p.rowData ?? []));
  }

  return results.flat();
}

async function fetchFlatGroupedSsrmRows<TRow>(
  fetchPage: (startRow: number, endRow: number) => Promise<SsrmMetricsResponse<TRow>>,
): Promise<TRow[]> {
  const rows = await fetchAllSsrmRows(fetchPage);
  if (rows.length > 0 && rows.every(row => (row as { childCount?: number }).childCount === undefined)) {
    throw new Error(
      'FLAT grouping response has no childCount on any row; the backend does not appear to support groupingMode=FLAT.',
    );
  }
  return rows;
}

function buildSsrmRequest(
  filterModel: SsrmFilterModel,
  startRow: number,
  endRow: number,
  sortColId: string,
  rowGroupCols: SsrmColumnVO[] = [],
  valueCols: SsrmColumnVO[] = [],
  groupingMode?: 'FLAT' | 'HIERARCHICAL',
  havingModel?: SsrmFilterModel,
): SsrmRequestBody {
  return {
    startRow,
    endRow,
    rowGroupCols,
    groupKeys: [],
    valueCols,
    pivotCols: [],
    pivotMode: false,
    filterModel,
    sortModel: [{ colId: sortColId, sort: 'asc' }],
    convertedFilterModel: convertFilterModelToBackend(filterModel as FilterModel),
    ...(groupingMode !== undefined && { groupingMode }),
    ...(havingModel !== undefined && { havingModel }),
  };
}

export interface CncAgaMetricsKpiResponse {
  activeFacilities: AgaMetricsKpiValue;
  newFacilitiesMtd: AgaMetricsKpiValue;
  terminationsMtd: AgaMetricsKpiValue;
}

export interface AgaMetricsKpiValue {
  current: number;
  priorMonth: number;
}

export interface AgaMetricsDateRange {
  start: Date;
  end: Date;
  label: string;
}

export interface MonthlyFacilitySubmitterRow {
  month: string;
  internalFacilities: number;
  externalFacilities: number;
  unknownFacilities: number;
}

export interface CncFacilitySubmitterMetricsResponse {
  months: MonthlyFacilitySubmitterRow[];
}

function formatDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

class CncSummaryMetricsService extends BaseService {
  constructor() {
    super('/api/funding-service');
  }

  getSsrmPage = (request: SsrmRequestBody): Promise<SsrmMetricsResponse<CncSummaryMetricsRow>> =>
    this.post<SsrmRequestBody, SsrmMetricsResponse<CncSummaryMetricsRow>>({
      path: 'cncsummary/ssrm',
      body: request,
    });

  fetchRows = (
    filterModel: SsrmFilterModel,
    rowGroupCols: SsrmColumnVO[] = [],
    valueCols: SsrmColumnVO[] = [],
  ): Promise<CncSummaryMetricsRow[]> =>
    fetchAllSsrmRows((startRow, endRow) =>
      this.getSsrmPage(buildSsrmRequest(filterModel, startRow, endRow, 'submissionDate', rowGroupCols, valueCols)),
    );

  fetchGroupedRows = (
    filterModel: SsrmFilterModel,
    groupCols: SsrmColumnVO[],
    valueCols: SsrmColumnVO[],
    havingModel?: SsrmFilterModel,
  ): Promise<CncSummaryMetricsRow[]> =>
    fetchFlatGroupedSsrmRows((startRow, endRow) =>
      this.getSsrmPage(
        buildSsrmRequest(filterModel, startRow, endRow, 'submissionDate', groupCols, valueCols, 'FLAT', havingModel),
      ),
    );

  fetchFacilitySubmitterMetrics = (range: AgaMetricsDateRange): Promise<MonthlyFacilitySubmitterRow[]> => {
    const startDate = formatDateInputValue(range.start);
    const endDate = formatDateInputValue(range.end);

    return this.get<CncFacilitySubmitterMetricsResponse>({
      path: 'cncsummary/facility-submitter-metrics',
      params: { startDate, endDate },
    }).then(response => response.months ?? []);
  };

  fetchAgaMetricsKpis = (
    referenceDate: Date,
    businessLine: 'cnc' | 'resi' = 'cnc',
  ): Promise<CncAgaMetricsKpiResponse> => {
    const referenceDateParam = formatDateInputValue(referenceDate);
    const business = businessLine === 'resi' ? 'RESI' : 'CNC';
    return this.get<CncAgaMetricsKpiResponse>({
      path: 'cncsummary/aga-metrics-kpis',
      params: { referenceDate: referenceDateParam, business },
    });
  };
}

export default CncSummaryMetricsService;
